import { forwardRef } from "react";
import { formatTurkishDate, TR_MONTHS, resolveUploadUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import CategoryIcon from "@/components/CategoryIcon";

// A printable, shareable card that lists the upcoming approved events.
// Only shows events with a start datetime greater than or equal to "now".
const UpcomingListCard = forwardRef(({ events = [], limit = 50 }, ref) => {
  const { categoryMap, settings } = useSettings();

  const now = new Date();
  const upcoming = (events || [])
    .filter((ev) => {
      const d = new Date(ev.event_datetime);
      return !isNaN(d.getTime()) && d >= now;
    })
    .sort((a, b) => new Date(a.event_datetime) - new Date(b.event_datetime))
    .slice(0, limit);

  return (
    <div
      ref={ref}
      data-testid="upcoming-list-card"
      className="social-card-bg relative w-full max-w-lg mx-auto overflow-hidden rounded-2xl shadow-2xl px-6 py-8 sm:px-8"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-[#8A252C]" />
      <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-[#8A252C]" />
      <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-[#8A252C]" />
      <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-[#8A252C]" />

      <div className="relative z-10">
        <div className="text-center flex flex-col items-center">
          {settings?.logo_url && (
            <img
              src={resolveUploadUrl(settings.logo_url)}
              alt={settings.site_name}
              crossOrigin="anonymous"
              className="w-16 h-16 rounded-xl object-cover shadow-md ring-2 ring-[#D4AF37]/40 bg-white mb-3"
            />
          )}
          <div className="inline-block text-[10px] font-bold tracking-[0.3em] uppercase px-3 py-1 rounded-full text-[#8A252C] bg-[#8A252C]/10 border border-[#8A252C]/30">
            {settings?.site_name || "ŞAHDER"} · Etkinlik Listesi
          </div>
          <h2
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            className="mt-3 text-[26px] font-bold text-[#1F2937] leading-tight"
          >
            Yaklaşan Etkinlikler
          </h2>
          <p className="text-xs text-[#4B5563] mt-1">
            {upcoming.length} etkinlik · {formatTurkishDate(now.toISOString()).split(" · ")[0]} itibariyle
          </p>
        </div>

        <div className="mt-5 space-y-2.5">
          {upcoming.length === 0 && (
            <div className="text-center text-sm text-[#4B5563] py-6">
              Şu anda kayıtlı yaklaşan etkinlik yok.
            </div>
          )}
          {upcoming.map((ev) => {
            const cat = categoryMap[ev.category];
            const d = new Date(ev.event_datetime);
            const day = String(d.getDate()).padStart(2, "0");
            const monShort = TR_MONTHS[d.getMonth()].slice(0, 3);
            const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
            return (
              <div
                key={ev.id}
                className="flex items-center gap-3 bg-white/85 rounded-xl px-3 py-2 border border-stone-200"
              >
                <div
                  className="w-12 h-14 rounded-lg flex flex-col items-center justify-center shrink-0"
                  style={{
                    backgroundColor: `${cat?.accent}14`,
                    border: `1px solid ${cat?.accent}30`,
                  }}
                >
                  <div
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", color: cat?.accent }}
                    className="text-[20px] font-bold leading-none"
                  >
                    {day}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest font-semibold text-[#4B5563] mt-0.5">
                    {monShort}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-[9px] uppercase tracking-widest font-bold inline-flex items-center gap-1"
                      style={{ color: cat?.accent }}
                    >
                      <CategoryIcon icons={cat?.icons} />
                      {cat?.label || ev.category}
                    </span>
                    <span className="ml-auto text-[10px] font-semibold text-[#1F2937]">{time}</span>
                  </div>
                  <div className="text-sm font-bold text-[#1F2937] leading-tight mt-0.5 break-words">
                    {ev.whose_event}
                  </div>
                  <div className="text-[11px] text-[#4B5563] break-words">
                    {ev.venue}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t border-[#8A252C]/20 text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8A252C]/80">
            Bir Aradayız, Bir Aradasınız
          </div>
        </div>
      </div>
    </div>
  );
});

UpcomingListCard.displayName = "UpcomingListCard";
export default UpcomingListCard;
